import type { RefObject } from 'react'
import { MessageSquare } from 'lucide-react'
import { NavigationButtons } from '@/components/ui/navigation-buttons'
import { AddressBar, type BreadcrumbSegment } from '@/components/ui/address-bar'
import { testIds } from '@/lib/test-ids'
import { openSidebarMode } from '@/lib/sidebar-mode'
import { dispatchOpenInboxMain } from '@/components/features/inbox/inbox-folder-types'
import { ThreadActionsMenu } from '@/components/features/inbox/InboxFeed'
import { useInboxThreadChromeActions } from '@/components/features/inbox/inbox-thread-chrome-context'

export type InboxThreadToolbarProps = {
  toolbarRef: RefObject<HTMLElement | null>
  title: string
  containerWidth: number | null
  canGoBack: boolean
  canGoForward: boolean
  onGoBack: () => void
  onGoForward: () => void
}

/**
 * Single-row continuity chrome for inbox-thread tabs:
 * [ < > | Inbox / {title} | Discuss? | ... ]
 *
 * Guide: docs/design/maater-list-surface-continuity.md (Reader chrome)
 */
export function InboxThreadToolbar({
  toolbarRef,
  title,
  containerWidth,
  canGoBack,
  canGoForward,
  onGoBack,
  onGoForward,
}: InboxThreadToolbarProps) {
  const chromeActions = useInboxThreadChromeActions()

  const returnToInbox = () => {
    openSidebarMode('inbox')
    dispatchOpenInboxMain()
  }

  const breadcrumbSegments: BreadcrumbSegment[] = [
    {
      label: 'Inbox',
      path: null,
      onClick: returnToInbox,
    },
    {
      label: title || 'Thread',
      path: null,
    },
  ]

  return (
    <nav
      ref={toolbarRef}
      className="flex items-center gap-2 px-4 py-2 border-b border-border bg-background/50 print-hide document-viewer-toolbar"
      aria-label="Inbox thread"
      data-testid={testIds.inbox.threadToolbar}
    >
      <NavigationButtons
        onGoBack={onGoBack}
        onGoForward={onGoForward}
        canGoBack={canGoBack}
        canGoForward={canGoForward}
        backTestId={testIds.documentViewer.backButton}
        forwardTestId={testIds.documentViewer.forwardButton}
      />

      <div className="min-w-0 flex-1">
        <AddressBar segments={breadcrumbSegments} containerWidth={containerWidth} />
        <span className="sr-only" data-testid={testIds.inbox.threadToolbarTitle}>
          {title}
        </span>
      </div>

      {chromeActions?.showThreadActions ? (
        <div className="flex shrink-0 items-center gap-1">
          {chromeActions.primaryActionLabel === 'Discuss' ? (
            <button
              type="button"
              onClick={chromeActions.onReply}
              className="flex items-center gap-1.5 rounded border border-border px-2.5 py-1 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              data-testid={testIds.inbox.soulProposalDiscuss}
            >
              <MessageSquare className="h-3.5 w-3.5" /> Discuss
            </button>
          ) : null}
          <ThreadActionsMenu
            threadId={chromeActions.threadId}
            threadTitle={chromeActions.threadTitle}
            threadStatus={chromeActions.threadStatus}
            linkedConversationId={chromeActions.linkedConversationId}
            surface="reader"
            onSnooze={chromeActions.onSnooze}
            onArchive={chromeActions.onArchive}
            onMarkAsUnread={chromeActions.onMarkAsUnread}
            onMarkAsRead={chromeActions.onMarkAsRead}
            onOpenInChat={chromeActions.onOpenInChat}
            onOpenInNewTab={chromeActions.onOpenInNewTab}
          />
        </div>
      ) : null}
    </nav>
  )
}
